import { motion } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { Zap, ShieldCheck, Download } from 'lucide-react';

const icons = [Zap, ShieldCheck, Download];

const cardVariants = {
  hidden: { opacity: 0, y: 50 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, delay: i * 0.15, ease: [0.16, 1, 0.3, 1] },
  }),
};

export default function FeaturesSection() {
  const { t } = useLanguage();

  return (
    <section id="features" className="py-40 relative overflow-hidden">
      {/* Divider */}
      <motion.div
        initial={{ scaleY: 0 }}
        whileInView={{ scaleY: 1 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true }}
        className="absolute top-0 left-1/2 -translate-x-1/2 w-px h-24 bg-gradient-to-b from-white/25 to-transparent origin-top"
      />

      {/* Animated background grid */}
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.025]"
        style={{
          backgroundImage:
            'linear-gradient(rgba(255,255,255,1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,1) 1px, transparent 1px)',
          backgroundSize: '60px 60px',
        }}
      />

      {/* Background orb */}
      <motion.div
        animate={{ scale: [1, 1.3, 1], opacity: [0.03, 0.06, 0.03] }}
        transition={{ duration: 8, repeat: Infinity }}
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full pointer-events-none"
        style={{ background: 'radial-gradient(circle, rgba(255,255,255,0.08) 0%, transparent 70%)', filter: 'blur(60px)' }}
      />

      <div className="max-w-7xl mx-auto px-6 lg:px-8 flex flex-col items-center text-center relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          viewport={{ once: true }}
          className="mb-20"
        >
          <p className="font-inter text-xs tracking-[0.3em] text-zinc-700 uppercase mb-4">Platform</p>
          <h2 className="font-outfit text-4xl sm:text-5xl font-light tracking-tight text-white">
            {t.features.heading}
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 w-full">
          {t.features.items.map((item, i) => {
            const Icon = icons[i];
            return (
              <motion.div
                key={i}
                data-testid={`feature-card-${i}`}
                custom={i}
                variants={cardVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                whileHover={{
                  y: -8,
                  boxShadow: '0 0 50px rgba(255,255,255,0.08), 0 0 100px rgba(255,255,255,0.03)',
                  borderColor: 'rgba(255,255,255,0.25)',
                }}
                className="group relative flex flex-col items-center text-center p-10 rounded-3xl
                  border border-white/10 backdrop-blur-2xl cursor-default
                  transition-colors duration-500"
                style={{ background: 'rgba(255,255,255,0.02)' }}
              >
                {/* Card inner glow on hover */}
                <div className="absolute inset-0 rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
                  style={{ background: 'radial-gradient(circle at 50% 0%, rgba(255,255,255,0.05) 0%, transparent 60%)' }}
                />

                <motion.div
                  whileHover={{ scale: 1.1, rotate: 5 }}
                  transition={{ type: 'spring', stiffness: 300 }}
                  className="w-12 h-12 rounded-2xl border border-white/10 bg-white/5 flex items-center justify-center mb-6 group-hover:border-white/30 group-hover:bg-white/10 transition-all duration-300"
                >
                  <Icon size={20} className="text-white" strokeWidth={1.5} />
                </motion.div>
                <h3 className="font-outfit text-xl font-medium text-white mb-3">{item.title}</h3>
                <p className="font-inter text-sm text-zinc-500 leading-relaxed">{item.desc}</p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
